import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { render } from 'react-dom';
import axios from './axios';
import { KeyValue } from './types/KeyValue';
import { handleBrowserBack } from './BrowserBack';
import DialogConfirm from './molecules/DialogConfirm';
import ResultDialog from './molecules/Dialog';
import LoadingModal from './molecules/LoadingModal';
import Messages from './molecules/Messages';
import { inputAssistDigit } from './Convert';

const Debug: React.FC = () => {
  // 検索フォームの現在の入力状態
  const [search_reception_cd, setSearchReceptionCd] = useState('');
  const [search_reception_sub_cd, setSearchReceptionSubCd] = useState('01');
  const [search_type_items, setSearchTypeItems] = useState<KeyValue[]>([]);
  const [search_type, setSearchType] = useState('');
  // 検索ボタン押下時の検索ステータス
  const [searching_params, setSearchingParams] = useState({
    reception_cd: '',
    reception_sub_cd: '',
    type: '',
  });
  // 検索結果
  const [result, setResult] = useState({
    id: '',
    value: '',
  });
  // 接続中ステータス
  const [is_connecting, setIsConnecting] = useState(true);
  // エラーメッセージ
  const [error_message, setErrorMessage] = useState('');
  // ダイアログの表示制御
  const [is_confirm_show, setIsConfirmShow] = useState(false);
  const [is_dialog_show, setIsDialogShow] = useState(false);
  const [dialog_message, setDialogMessage] = useState('');

  // onload
  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    const getProjectTypes = async () => {
      try {
        const response = await axios.get(`${location.origin}${process.env.MIX_MOCK_URL}/debug/list`);
        setSearchTypeItems(response.data.projects_col_keys);
        // 項目の初期値を設定する
        setSearchType(response.data.projects_col_keys[0].key);
      } catch (error) {
        console.error(error.message);
      } finally {
        setIsConnecting(false);
      }
    };
    // Ajaxリクエスト
    getProjectTypes();
  }, []);

  // 検索フォームの変更イベント
  const handleChangeSearchReceptionCd = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchReceptionCd(event.target.value);
  };
  const handleBlurSearchReceptionCd = () => {
    setSearchReceptionCd(inputAssistDigit(search_reception_cd));
  };
  const handleChangeSearchReceptionSubCd = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchReceptionSubCd(event.target.value);
  };
  const handleChangeSearchType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(event.target.value);
  };
  // 検索結果の変更イベント
  const handleChangeResult = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newResult = result;
    newResult.value = event.target.value;
    setResult(Object.assign({}, newResult));
  };

  // 検索ボタン押下
  const handleClickSearch = () => {
    // バリデーション
    if (!isValidInput()) {
      return false;
    }

    // エラーメッセージを削除
    setErrorMessage('');

    // 検索中フラグを立てる
    setIsConnecting(true);

    // 検索を実行する
    searchReception();
  };

  // 検索処理
  const searchReception = () => {
    const search = async () => {
      try {
        const params = {
          reception_cd: search_reception_cd,
          reception_sub_cd: search_reception_sub_cd,
          type: search_type,
        };
        const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/debug/search`, params);

        // 案件が見つからなかった場合
        if (!response.data.result) {
          setErrorMessage(response.data.message);
          return false;
        }

        // 検索結果を反映
        const newResult = result;
        newResult.id = response.data.id;
        newResult.value = response.data.value !== null ? response.data.value : '';
        setResult(Object.assign({}, newResult));
        // 案件が見つかったら検索中コードをstateに保持する
        setSearchingParams(params);
      } catch (error) {
        console.error(error.message);
        setErrorMessage('案件が見つかりませんでした。');
      } finally {
        setIsConnecting(false);
      }
    };
    search();
  };

  // バリデーション
  const isValidInput = () => {
    if (
      search_reception_cd === null ||
      typeof search_reception_cd === 'undefined' ||
      search_reception_cd.length === 0
    ) {
      setErrorMessage('案件番号を入力して下さい。');
      return false;
    }
    if (
      search_reception_sub_cd === null ||
      typeof search_reception_sub_cd === 'undefined' ||
      search_reception_sub_cd.length === 0
    ) {
      setErrorMessage('案件枝番号を入力して下さい。');
      return false;
    }
    if (search_type === null || typeof search_type === 'undefined' || search_type.length === 0) {
      setErrorMessage('項目を入力して下さい。');
      return false;
    }
    return true;
  };

  // 更新ボタン押下
  const handleClickUpdate = () => {
    setErrorMessage('');
    setIsConfirmShow(true);
  };

  // 確認ダイアログ
  // はいボタン
  const handleConfirmYes = () => {
    const update = async () => {
      let message = null;
      try {
        const params = {
          rec_id: result.id,
          col_name: searching_params.type,
          value: result.value,
        };
        const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/debug/update`, params);
        message = response.data.message;
      } catch (error) {
        console.error(error.message);
        message = '更新に失敗しました。';
      } finally {
        setIsConnecting(false);
        setDialogMessage(message);
        setIsDialogShow(true);
      }
    };
    setIsConfirmShow(false);
    setIsConnecting(true);
    update();
  };

  return (
    <div className="container">
      <div className="announce">
        <p>案件番号と案件枝番号と項目を入力し、更新対象を選択して下さい。</p>
      </div>
      <div className="search row">
        <div className="col-md-3">
          <span>案件番号</span>
          <input
            type="text"
            value={search_reception_cd}
            onChange={handleChangeSearchReceptionCd}
            onBlur={handleBlurSearchReceptionCd}
          />
        </div>
        <div className="col-md-2">
          <span>案件枝番号</span>
          <select value={search_reception_sub_cd} onChange={handleChangeSearchReceptionSubCd}>
            <option value="01">01</option>
            <option value="02">02</option>
          </select>
        </div>
        <div className="col-md-5">
          <span>項目</span>
          <select defaultValue={search_type} onChange={handleChangeSearchType}>
            {search_type_items?.map((option, index) => {
              return (
                <option key={index} value={option.key}>
                  {option.value}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-md-2">
          <Button onClick={handleClickSearch}>検索</Button>
        </div>
      </div>
      <div className="messages">
        <Messages valid_msg={error_message} />
      </div>
      <div className="result row">
        <div className="result-body col-md-10">
          <span>検索結果</span>
          <textarea value={result.value} onChange={handleChangeResult} />
        </div>
        <div className="update col-md-2">
          <Button className="update-button" onClick={handleClickUpdate}>
            更新
          </Button>
        </div>
      </div>
      {/* 更新確認モーダル */}
      <DialogConfirm
        label="更新確認"
        body={<p>更新しますか？</p>}
        ok="はい"
        okCallback={handleConfirmYes}
        cancel="いいえ"
        cancelCallback={() => {
          setIsConfirmShow(false);
        }}
        open={is_confirm_show}
      />
      {/* 更新結果モーダル */}
      <ResultDialog
        label="更新結果"
        body={<p>{dialog_message}</p>}
        ok={'OK'}
        okCallback={() => {
          setIsDialogShow(false);
        }}
        open={is_dialog_show}
      />
      {/* ローディング画面 */}
      <LoadingModal show={is_connecting} />
    </div>
  );
};
if (document.getElementById('debug')) {
  render(<Debug />, document.getElementById('debug'));
}
